/**
 * Mappträd inne i appen (motsvarar strukturen som används mot SharePoint/OneDrive).
 *
 * Varje nytt projekt får samma skedesindelning som Bas-processen, så att
 * dokument hamnar på ett förutsägbart ställe. Trädet är hjälp – inte tvång:
 * innehåll kan fortfarande ligga direkt i projektet eller i egna mappar.
 */
import { supabase } from "@/integrations/supabase/client";
import { phaseForDoc } from "@/lib/integrations/folderTree";

export const PROJECT_PHASE_FOLDERS = [
  "01 Planering",
  "02 Projektering",
  "03 Utförande",
  "04 Bruksskede",
] as const;

export type ProjectPhaseFolder = (typeof PROJECT_PHASE_FOLDERS)[number];

/** Vilken skedesmapp ett dokument hör hemma i. */
export const folderNameForDoc = (
  docNumber: string | null | undefined,
  documentType?: string | null,
): ProjectPhaseFolder => {
  const type = (documentType ?? "").toLowerCase();
  if (type.includes("arbetsberedning")) return "03 Utförande";
  if (type.includes("bruksskede")) return "04 Bruksskede";
  return PROJECT_PHASE_FOLDERS[phaseForDoc(docNumber) - 1];
};

interface FolderRow {
  id: string;
  name: string;
}

async function loadRootFolders(projectId: string): Promise<FolderRow[]> {
  const { data } = await supabase
    .from("folders")
    .select("id, name")
    .eq("project_id", projectId)
    .is("parent_folder_id", null);
  return (data as FolderRow[]) || [];
}

/**
 * Skapar projektets skedesmappar. Idempotent – befintliga mappar lämnas orörda.
 * Returnerar namn → mapp-id.
 */
export async function createProjectFolderTree(
  projectId: string,
  userId: string,
): Promise<Record<string, string>> {
  const existing = await loadRootFolders(projectId);
  const byName: Record<string, string> = {};
  existing.forEach((f) => {
    byName[f.name.toLowerCase()] = f.id;
  });

  const missing = PROJECT_PHASE_FOLDERS.filter((name) => !byName[name.toLowerCase()]);
  if (missing.length > 0) {
    const { data, error } = await supabase
      .from("folders")
      .insert(
        missing.map((name) => ({
          name,
          description: null,
          project_id: projectId,
          parent_folder_id: null,
          created_by: userId,
        })),
      )
      .select("id, name");

    if (!error && data) {
      (data as FolderRow[]).forEach((f) => {
        byName[f.name.toLowerCase()] = f.id;
      });
    }
  }

  const result: Record<string, string> = {};
  PROJECT_PHASE_FOLDERS.forEach((name) => {
    const id = byName[name.toLowerCase()];
    if (id) result[name] = id;
  });
  return result;
}

/** Hämtar (eller skapar) rätt skedesmapp för ett dokument i ett projekt. */
export async function ensurePhaseFolder(
  projectId: string,
  userId: string,
  docNumber: string | null | undefined,
  documentType?: string | null,
): Promise<string | null> {
  const wanted = folderNameForDoc(docNumber, documentType);
  const existing = await loadRootFolders(projectId);
  const found = existing.find((f) => f.name.toLowerCase() === wanted.toLowerCase());
  if (found) return found.id;

  const tree = await createProjectFolderTree(projectId, userId);
  return tree[wanted] ?? null;
}

/** Alla mappar i ett projekt, för väljare i UI. */
export async function listProjectFolders(projectId: string): Promise<FolderRow[]> {
  const { data } = await supabase
    .from("folders")
    .select("id, name")
    .eq("project_id", projectId)
    .order("name", { ascending: true });
  return (data as FolderRow[]) || [];
}
